import { useEffect, useState } from 'hono/jsx';
import { SiteShell } from '../../components/SiteShell';
import { ButtonSpinner } from '../../components/Spinner';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { navigate } from '../../lib/router';
import { useSite } from '../../lib/site';

const MAX_PRE_PROMPT = 4000;

function mainDomain(): string {
  const h = window.location.hostname;
  return h.endsWith('webforcheap.com') ? 'webforcheap.com' : 'appsforcheap.com';
}

export function SiteSettings({ siteId }: { siteId: string }) {
  const { site, loading, reload } = useSite(siteId);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [prePrompt, setPrePrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);
  const [keyPrefix, setKeyPrefix] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);

  useEffect(() => {
    if (site) { setName(site.name || ''); setEmail(site.email || ''); setPrePrompt(site.prePrompt || ''); }
  }, [site?.id, site?.updatedAt]);

  // Load whether an access key already exists (prefix only — the raw key is never
  // retrievable after creation).
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/sites/' + siteId + '/keys');
        if (!r.ok) return;
        const data = await r.json().catch(() => ({}));
        const keys = (data as any)?.keys || [];
        if (!cancelled) setKeyPrefix(keys.length ? keys[0].prefix : null);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  async function onCreateKey() {
    if (!site || creatingKey) return;
    if (keyPrefix && !confirm(
      'Create a new access key?\n\n' +
      'This revokes the current key (' + keyPrefix + '…). Any agent still using the ' +
      'old key will stop working until you give it the new one.'
    )) return;
    setCreatingKey(true);
    try {
      const r = await api('/sites/' + siteId + '/keys', { method: 'POST' });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error((err as any).error || 'Failed to create key'); }
      const data = await r.json();
      setNewKey((data as any).token);
      setKeyPrefix((data as any).prefix);
      showToast('success', 'Access key created', 'Copy it now — it will not be shown again.');
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to create key');
    } finally {
      setCreatingKey(false);
    }
  }

  async function copyKey() {
    if (!newKey) return;
    try {
      await navigator.clipboard.writeText(newKey);
      showToast('success', 'Copied', 'Access key copied to clipboard');
    } catch {
      showToast('error', 'Copy failed', 'Select the key and copy it manually');
    }
  }

  async function onSave(e: Event) {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api('/sites/' + siteId, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, prePrompt }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error((err as any).error || 'Failed to update site'); }
      showToast('success', 'Settings saved', 'Your changes have been saved');
      await reload();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to update site');
    } finally {
      setSaving(false);
    }
  }

  async function onDownloadSkill() {
    if (!site) return;
    if (downloading) return;
    setDownloading(true);
    try {
      const r = await api('/sites/' + siteId + '/skill');
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error((err as any).error || 'Failed to generate skill');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `webforcheap-${site.slug}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast('success', 'Skill downloaded', 'No secret inside — set your access key via WFC_TOKEN.');
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to generate skill');
    } finally {
      setDownloading(false);
    }
  }

  async function onDelete() {
    if (!site) return;
    if (!confirm(`Are you sure you want to delete "${site.name}"? This action cannot be undone.`)) return;
    try {
      const r = await api('/sites/' + siteId, { method: 'DELETE' });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error((err as any).error || 'Failed to delete site'); }
      navigate('/sites');
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to delete site');
    }
  }

  return (
    <SiteShell site={site} loading={loading} active="settings">
      <div class="space-y-6">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
            <h2 class="text-lg font-semibold text-slate-100 mb-4">General</h2>
            <form onSubmit={onSave} class="space-y-5">
              <div>
                <label for="settingsName" class="block text-sm font-medium text-slate-300 mb-1.5">Site Name</label>
                <input
                  id="settingsName" type="text" required maxLength={100}
                  value={name}
                  onInput={(e: Event) => setName((e.currentTarget as HTMLInputElement).value)}
                  class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1.5">Subdomain</label>
                <div class="flex">
                  <input
                    type="text" disabled value={site?.slug || ''}
                    class="flex-1 px-3 py-2 bg-dark-900 border border-r-0 border-slate-700/50 text-slate-500 rounded-l-lg text-sm cursor-not-allowed"
                  />
                  <span class="inline-flex items-center px-3 py-2 border border-slate-700/50 bg-dark-900 text-slate-500 text-sm rounded-r-lg">.{mainDomain()}</span>
                </div>
                <p class="text-xs text-slate-600 mt-1.5">Subdomain cannot be changed after creation</p>
              </div>
              <div>
                <label for="settingsEmail" class="block text-sm font-medium text-slate-300 mb-1.5">Contact Email</label>
                <input
                  id="settingsEmail" type="email" required
                  value={email}
                  onInput={(e: Event) => setEmail((e.currentTarget as HTMLInputElement).value)}
                  class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                />
                <p class="text-xs text-slate-500 mt-1.5">Used for contact form submissions and Spark builds</p>
              </div>
              <div class="flex justify-end pt-2">
                <button type="submit" disabled={saving} class="px-4 py-2 text-sm font-medium text-dark-900 bg-cyan-500 hover:bg-cyan-400 rounded-lg transition-colors disabled:opacity-60">
                  {saving ? <ButtonSpinner /> : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>

          <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
            <h2 class="text-lg font-semibold text-slate-100 mb-1">Quick actions</h2>
            <p class="text-sm text-slate-400 mb-4">Jump to the most common tasks for this site.</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <QuickLink to={`/sites/${siteId}`}            label="Build / Update"   sub="Create or change the site with AI" iconPath="M13 10V3L4 14h7v7l9-11h-7z" />
              <QuickLink to={`/sites/${siteId}/versions`}   label="Version History"  sub="Review and restore versions"        iconPath="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              <QuickLink to={`/sites/${siteId}/domain`}     label="Custom Domain"    sub="Connect your own domain"            iconPath="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3" />
              <QuickLink to={`/sites/${siteId}/materials`}  label="Brand Materials"  sub="Logos and assets for builds"        iconPath="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              <QuickLink to={`/sites/${siteId}/backups`}    label="Backups"          sub="Download ZIP & Drive sync"          iconPath="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              <QuickLink to={`/sites/${siteId}/billing`}    label="Billing"          sub="Plans & payment links"              iconPath="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </div>
          </div>
        </div>

        <details class="group bg-dark-800 border border-slate-700/50 rounded-xl overflow-hidden">
          <summary class="flex items-center justify-between cursor-pointer select-none list-none px-6 py-4 hover:bg-dark-700/40 transition-colors group-open:border-b group-open:border-slate-700/50 [&::-webkit-details-marker]:hidden">
            <span class="flex items-center gap-2">
              <svg class="w-4 h-4 text-slate-400 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
              <span class="text-lg font-semibold text-slate-100">Advanced</span>
            </span>
            <span class="text-xs text-slate-500 hidden sm:block">Custom instructions · AI agent access</span>
          </summary>

          <div class="p-6 space-y-8">
            <section>
          <h2 class="text-lg font-semibold text-slate-100 mb-1">Custom instructions</h2>
          <p class="text-sm text-slate-400 mb-4 max-w-3xl">
            Tell us anything that should always be true about your site. We follow these instructions
            <span class="text-slate-300"> every time your site is built or updated</span>, so you only have to
            say them once. For example:
          </p>
          <ul class="text-sm text-slate-400 mb-4 space-y-1 list-disc pl-5 max-w-3xl">
            <li>Keep the writing friendly and easy to read.</li>
            <li>Always show our phone number and opening hours in the footer.</li>
            <li>Add our analytics or tracking code to every page.</li>
          </ul>
          <form onSubmit={onSave} class="space-y-3">
            <div>
              <textarea
                id="settingsPrePrompt" rows={8} maxLength={MAX_PRE_PROMPT}
                value={prePrompt}
                onInput={(e: Event) => setPrePrompt((e.currentTarget as HTMLTextAreaElement).value)}
                placeholder={'e.g. Always mention our free consultation offer on the homepage.\nKeep the tone warm and simple.\nAdd our tracking code to every page.'}
                class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
              />
              <div class="flex items-center justify-between mt-1.5 gap-4">
                <p class="text-xs text-slate-500">
                  Plain English works great — you can also paste code (like a tracking snippet) and we'll use it
                  exactly as written. Leave blank for none.
                  <span class="text-slate-600"> (Developers: this is your site's pre-prompt.)</span>
                </p>
                <p class="text-xs text-slate-600 tabular-nums shrink-0">{prePrompt.length}/{MAX_PRE_PROMPT}</p>
              </div>
            </div>
            <div class="flex justify-end">
              <button type="submit" disabled={saving || !site} class="px-4 py-2 text-sm font-medium text-dark-900 bg-cyan-500 hover:bg-cyan-400 rounded-lg transition-colors disabled:opacity-60">
                {saving ? <ButtonSpinner /> : 'Save instructions'}
              </button>
            </div>
          </form>
            </section>

            <section class="border-t border-slate-700/50 pt-8 space-y-6">
          <div>
            <h2 class="text-lg font-semibold text-slate-100 mb-1">Control this site with an AI agent</h2>
            <p class="text-sm text-slate-400 max-w-2xl">
              Point Claude Code (or any compatible agent) at this site to edit pages, upload
              materials, run builds, and publish. Two steps: create an access key, then download
              the skill file.
            </p>
          </div>

          {/* Step 1 — access key */}
          <div class="border-t border-slate-700/50 pt-5">
            <h3 class="text-sm font-semibold text-slate-200 mb-1">1. Access key</h3>
            <p class="text-xs text-slate-500 mb-3 max-w-2xl">
              A site-scoped secret with full control of this site. It's shown once on creation.
              Creating a new key revokes the previous one.
              {keyPrefix && !newKey ? <span class="text-slate-400"> Current key: <code class="text-slate-300">{keyPrefix}…</code></span> : null}
            </p>

            {newKey ? (
              <div class="mb-3">
                <div class="flex gap-2">
                  <input
                    type="text" readOnly value={newKey}
                    onFocus={(e: Event) => (e.currentTarget as HTMLInputElement).select()}
                    class="flex-1 px-3 py-2 bg-dark-900 border border-cyan-500/40 rounded-lg text-sm text-cyan-300 font-mono"
                  />
                  <button type="button" onClick={copyKey} class="px-3 py-2 text-sm font-medium text-dark-900 bg-cyan-500 hover:bg-cyan-400 rounded-lg transition-colors">Copy</button>
                </div>
                <p class="text-xs text-amber-400/90 mt-1.5">Copy this now — it won't be shown again. Set it as <code>WFC_TOKEN</code> where your agent runs.</p>
              </div>
            ) : null}

            <button
              type="button"
              onClick={onCreateKey}
              disabled={creatingKey || !site}
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-dark-900 bg-cyan-500 hover:bg-cyan-400 rounded-lg transition-colors disabled:opacity-60"
            >
              {creatingKey ? <ButtonSpinner /> : null}
              {keyPrefix ? 'Create new key' : 'Create access key'}
            </button>
          </div>

          {/* Step 2 — skill file */}
          <div class="border-t border-slate-700/50 pt-5">
            <h3 class="text-sm font-semibold text-slate-200 mb-1">2. Skill file</h3>
            <p class="text-xs text-slate-500 mb-3 max-w-2xl">
              Self-contained instructions + this site's IDs. <span class="text-slate-400">Contains no secret</span>,
              so it's safe to keep in a repo. Drop it in your agent's
              <code class="text-slate-300"> .claude/skills/</code> folder, then provide the key via the
              <code class="text-slate-300"> WFC_TOKEN</code> environment variable:
            </p>
            <pre class="text-xs bg-dark-900 border border-slate-700/50 rounded-lg p-3 mb-3 overflow-x-auto text-slate-300"><code>export WFC_TOKEN="wfc_...your key..."</code></pre>
            <p class="text-xs text-slate-500 mb-3 max-w-2xl">
              To customize behavior, add an <code class="text-slate-300">EXTEND.md</code> next to the
              skill — re-downloading the skill never overwrites it.
            </p>
            <button
              type="button"
              onClick={onDownloadSkill}
              disabled={downloading || !site}
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 border border-slate-600/50 hover:bg-dark-700 rounded-lg transition-colors disabled:opacity-60"
            >
              {downloading ? <ButtonSpinner /> : (
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                </svg>
              )}
              Download skill file
            </button>
          </div>
            </section>
          </div>
        </details>

        <div class="bg-dark-800 border border-red-500/20 rounded-xl p-6">
          <h2 class="text-lg font-semibold text-slate-100 mb-1">Danger zone</h2>
          <p class="text-sm text-slate-400 mb-4">Permanently delete this site and all of its files. This cannot be undone.</p>
          <button type="button" onClick={onDelete} class="px-4 py-2 text-sm font-medium text-red-400 border border-red-500/30 hover:bg-red-500/10 rounded-lg transition-colors">
            Delete Site
          </button>
        </div>
      </div>
    </SiteShell>
  );
}

function QuickLink({ to, label, sub, iconPath }: { to: string; label: string; sub: string; iconPath: string }) {
  return (
    <a
      href={to}
      onClick={(e: MouseEvent) => { e.preventDefault(); navigate(to); }}
      class="flex items-center gap-3 p-3 rounded-lg border border-slate-700/50 hover:border-cyan-500/40 hover:bg-dark-700 transition-colors"
    >
      <svg class="w-5 h-5 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={iconPath}/>
      </svg>
      <div class="min-w-0">
        <p class="text-sm font-medium text-slate-200">{label}</p>
        <p class="text-xs text-slate-500">{sub}</p>
      </div>
    </a>
  );
}
